import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { BaseLineLock } from '@/app/components/icons/BaseLineLock';
import { ProgressiveBlur } from '@/app/components/ui/progressive-blur';
import { MAX_PROMPT_LENGTH, SITE } from '@/app/constants';
import type {
  ElementModality,
  MentionItem,
  RecaptionPrompt,
  ReferencePrompt,
  TextPromptWithInputItem,
} from '@/app/types';
import { cn, getDefaultRecaptionPrompt, getTextPromptLength, stringifyTextPromptWithReference } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import { convertSimpleToProTextPrompt } from '../../utils';
import { PresetPromptsTags } from './components/PresetPromptsTags';
import { RichTextField } from './components/RichTextField';
import { EXAMPLE_PROMPT } from './constants';

interface ProTextareaProps {
  recaptionPrompt?: RecaptionPrompt;
  updateRecaptionPrompt: (value: Partial<RecaptionPrompt>, partial?: boolean) => void;
  allowMention?: boolean;
  onClear?: () => void;
  loading?: boolean;
  onBeforeAddMention?: (mentionItem: MentionItem) => boolean;
  onBeforeDeleteMention?: (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => boolean;
  onOpenPromptLibraryPopover?: () => void;
  getExtraItems?: () => ReferencePrompt[];
  shouldUpgrade?: boolean;
  onOpenPlanDialog?: () => void;
  /** 允许的 modality 筛选 */
  allowedModalities?: Array<ElementModality>;
}

export const ProTextarea: FC<ProTextareaProps> = ({
  recaptionPrompt,
  updateRecaptionPrompt,
  allowMention = false,
  onClear,
  loading,
  onBeforeAddMention,
  onBeforeDeleteMention,
  onOpenPromptLibraryPopover,
  getExtraItems,
  shouldUpgrade,
  onOpenPlanDialog,
  allowedModalities,
}) => {
  const locale = useLocale();
  const t = useTranslations('create.proMode');
  const { subject, environment, ...restPrompt } = recaptionPrompt || getDefaultRecaptionPrompt(t);

  // 追踪粘贴状态
  const [hasRecentStructuredPaste, setHasRecentStructuredPaste] = useState(false);
  const previousPromptRef = useRef<RecaptionPrompt | null>(null);

  // 处理撤销操作
  const handleUndo = (): boolean => {
    if (hasRecentStructuredPaste && previousPromptRef.current) {
      // 撤销整个粘贴行为，恢复到粘贴前的状态
      updateRecaptionPrompt(previousPromptRef.current);
      setHasRecentStructuredPaste(false);
      previousPromptRef.current = null;
      return true; // 表示已处理撤销操作
    }
    return false; // 表示未处理，使用默认撤销行为
  };

  // 当用户开始编辑任一输入框时，清除粘贴状态标记
  const handleContentChange = () => {
    if (hasRecentStructuredPaste) {
      setHasRecentStructuredPaste(false);
      previousPromptRef.current = null;
    }
  };

  const handleClear = () => {
    updateRecaptionPrompt({});
    onClear?.();
    // 清除粘贴状态
    setHasRecentStructuredPaste(false);
    previousPromptRef.current = null;
  };

  const handlePaste = (clipboardContent: string) => {
    // 如果已经有内容，则使用默认粘贴行为（直接插入到当前输入框）
    if (subject.length > 0 || environment.length > 0) {
      return false;
    }

    // 保存当前状态，用于撤销
    const currentPrompt = recaptionPrompt || getDefaultRecaptionPrompt(t);

    // 尝试 parse 文本
    const parsedPrompt = convertSimpleToProTextPrompt(clipboardContent, [], t);
    const { subject: _, environment: __, ...defaultPrompt } = getDefaultRecaptionPrompt(t);

    // 深比较除了 subject、environment 字段以外的其他值
    const {
      subject: _subject,
      environment: parsedEnvironment,
      ...parsedPromptWithoutSubjectAndEnvironment
    } = parsedPrompt;

    // 如果 environment 有内容，或者除了 subject、environment 字段以外的其他值与默认值不同，说明是结构化内容，使用解析结果
    if (
      stringifyTextPromptWithReference(parsedEnvironment) ||
      JSON.stringify(parsedPromptWithoutSubjectAndEnvironment) !== JSON.stringify(defaultPrompt)
    ) {
      // 保存粘贴前的状态
      previousPromptRef.current = currentPrompt;
      setHasRecentStructuredPaste(true);

      updateRecaptionPrompt(parsedPrompt);
      return true;
    }

    // 否则只是纯文本，使用默认粘贴逻辑
    return false;
  };

  if (loading) {
    return (
      <div className="relative mb-4 flex h-full flex-col pb-8">
        <div className="-translate-x-1/2 absolute top-0 left-1/2 z-10 flex h-9 items-center justify-center rounded-full bg-system-bg02 px-4 shadow-lg">
          <BaseLineLoading2 className="animate-spin text-base text-system-white" />
          <span className="ms-2 whitespace-nowrap text-system-white36">{t('optimizing')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2.5 w-8 animate-pulse rounded bg-gradient-to-r from-[#D9D9D9] to-[#737373] opacity-10" />
          <span className="text-system-danger/35 text-xs">*</span>
        </div>
        <div className="mb-1 flex items-center">
          <span className="text-system-white36 text-xl">{'['}</span>
          <div className="h-4 w-[120px] animate-pulse rounded bg-gradient-to-r from-[#D9D9D9] to-[#737373] opacity-10" />
          <span className="text-system-white36 text-xl">{'],'}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2.5 w-[50px] animate-pulse rounded bg-gradient-to-r from-[#D9D9D9] to-[#737373] opacity-10" />
          <span className="text-system-danger/35 text-xs">*</span>
        </div>
        <div className="flex flex-wrap items-center">
          <span className="text-system-white36 text-xl">{'['}</span>
          <div className="h-4 w-[calc(100%-10px)] animate-pulse rounded bg-gradient-to-r from-[#D9D9D9] to-[#737373] opacity-10" />
          <div className="h-4 w-[320px] animate-pulse rounded bg-gradient-to-r from-[#D9D9D9] to-[#737373] opacity-10" />
          <span className="text-system-white36 text-xl">{'],'}</span>
        </div>
        <div className="mt-2 flex gap-1">
          <div className="flex h-7 items-center justify-center rounded-8 bg-system-blue02/30 px-2 py-1.5">
            <div className="h-4 w-[64px] animate-pulse rounded bg-gradient-to-r from-[#D9D9D9] to-[#737373] opacity-10" />
          </div>
          <span className="text-system-white36 text-xl">,</span>
          <div className="flex h-7 items-center justify-center rounded-8 bg-system-blue02/30 px-2 py-1.5">
            <div className="h-4 w-[64px] animate-pulse rounded bg-gradient-to-r from-[#D9D9D9] to-[#737373] opacity-10" />
          </div>
          <div className="h-7 w-8 rounded-8 bg-system-white10" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative mb-4 flex h-full flex-col gap-1 pb-8">
      <div className="relative mb-1 text-base">
        <RichTextField
          field={t('subject.title')}
          info={t('subject.tip')}
          value={subject}
          setValue={(value) => {
            updateRecaptionPrompt(
              {
                subject: value,
              },
              true,
            );
            handleContentChange();
          }}
          maxLength={Math.max(0, MAX_PROMPT_LENGTH - getTextPromptLength(environment))}
          placeholder={EXAMPLE_PROMPT[SITE][locale]?.subject ?? ''}
          allowMention={allowMention}
          onBeforeAddMention={onBeforeAddMention}
          onBeforeDeleteMention={onBeforeDeleteMention}
          getExtraItems={getExtraItems}
          onPaste={handlePaste}
          onUndo={handleUndo}
          allowedModalities={allowedModalities}
        />
        <RichTextField
          field={t('environment.title')}
          info={t('environment.tip')}
          value={environment}
          setValue={(value) => {
            updateRecaptionPrompt(
              {
                environment: value,
              },
              true,
            );
            handleContentChange();
          }}
          maxLength={Math.max(0, MAX_PROMPT_LENGTH - getTextPromptLength(subject))}
          placeholder={EXAMPLE_PROMPT[SITE][locale]?.environment ?? ''}
          allowMention={allowMention}
          onBeforeAddMention={onBeforeAddMention}
          onBeforeDeleteMention={onBeforeDeleteMention}
          getExtraItems={getExtraItems}
          onPaste={handlePaste}
          onUndo={handleUndo}
          allowedModalities={allowedModalities}
        />
        {shouldUpgrade && (
          <div className="-top-1 -left-2 absolute h-[calc(100%+12px)] w-[calc(100%+16px)] rounded-8 border border-system-white10 bg-system-black48 p-2 backdrop-blur-sm">
            {/* 渐进式模糊层 */}
            <ProgressiveBlur />
            <div className="relative flex size-full flex-col items-center justify-center gap-2 rounded-4 border border-system-white/40 border-dashed">
              <div className="flex size-8 items-center justify-center rounded-8 bg-system-white20 backdrop-blur-sm">
                <BaseLineLock className="text-base" />
              </div>
              <span className="text-sm">
                {t.rich('unlockProMode', {
                  upgrade: (chunks) => (
                    <span className="cursor-pointer font-medium text-system-blue02" onClick={onOpenPlanDialog}>
                      {chunks}
                    </span>
                  ),
                })}
              </span>
            </div>
          </div>
        )}
      </div>

      <PresetPromptsTags
        promptValues={restPrompt}
        onDeleteTag={(key, value) => {
          updateRecaptionPrompt({ [key]: restPrompt[key]?.filter((t) => t !== value) }, true);
        }}
        onOpenPromptLibraryPopover={onOpenPromptLibraryPopover}
      />

      <div className="absolute right-0 bottom-0">
        <BaseLineDelete
          className={cn(
            'cursor-pointer text-base',
            shouldUpgrade && 'cursor-not-allowed text-system-white opacity-[.64]',
          )}
          onClick={!shouldUpgrade ? handleClear : undefined}
        />
      </div>
    </div>
  );
};
